import React, { ReactElement, WeakValidationMap } from 'react';
import PropTypes from 'prop-types';
import { forgeClassHelper } from '../utils/classes';

const classes = forgeClassHelper({ name: 'status-tag' });

/** A supported preset type string */
export type StatusTagType = 'gray' | 'new' | 'critical' | 'attention' | 'positive';
/** The list of supported preset types */
export const statusTagTypes: readonly StatusTagType[] = ['gray', 'new', 'critical', 'attention', 'positive'];

export interface StatusTagProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** set to true if you want background around status text. */
  background?: boolean;
  /** Sets the tag's base color. Set the secondary prop to reduce color's strength. */
  color?: StatusTagType;
  /** Adds a class to the root element of the component */
  className?: string;
  /** Set to true to use more muted tones. */
  secondary?: boolean;
  /** Sets the tag's height. */
  size?: 'compact' | 'medium';
  /** The string displayed in the component */
  text: string;
}

const StatusTag = ({
  background = true,
  className,
  text,
  secondary = false,
  color = 'gray',
  size,
  ...props
}: StatusTagProps): ReactElement => {
  return (
    <span
      role="note"
      {...props}
      {...classes({
        extra: className,
        modifiers: {
          [`${color}${secondary ? '-secondary' : ''}`]: background === true,
          nobackground: background === false,
          compact: size === 'compact',
        },
      })}
    >
      {text}
    </span>
  );
};

// propTypes comments are used to generate documentation and *must* be included
export const statusTagPropTypes: WeakValidationMap<StatusTagProps & { '...rest': unknown }> = {
  /** set to true if you want background around status text. */
  background: PropTypes.bool,

  /** The string displayed in the component */
  text: PropTypes.string.isRequired,

  /** Adds a class to the root element of the component */
  className: PropTypes.string,

  /** Sets the tag's base color. Set the secondary prop to reduce color's strength. */
  color: PropTypes.oneOf(statusTagTypes),

  /** Set to true to use more muted tones. */
  secondary: PropTypes.bool,

  /** Sets the tag's height. */
  size: PropTypes.oneOf(['compact', 'medium']),

  /** Additional props are passed to the component's root element. */
  '...rest': PropTypes.any,
};

StatusTag.propTypes = statusTagPropTypes;

export default StatusTag;
